import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppLiveChange, AppLiveEvents, AppLiveReaction } from "./AppLiveEvents";
import { AppBubbleKinds } from "./AppBubbleKinds";
import { AppStanding, AppStandingNow } from "./AppStanding";
import { AppWaLink, AppWaNow } from "./AppWaLink";

// AppLiveRules - NEW INCOMING MESSAGES IN THE OPEN CHAT WITHIN SECONDS: the page's half of `/live`, as rules
// with no transport and no DOM (step 1 of the translation feature, `documentation/design/translation.md`:
// "live incoming: new messages appear in the OPEN chat within seconds ... instead of up to 15 min").
//
// ==================================================================================================
// !! WHY A LONG POLL, NOT A SHORT ONE (measured - see `Bridge/src/LiveRun.ts`'s header for the numbers)
// ==================================================================================================
//
// The page asks `/live` "anything newer than X in this chat?"; the service answers AT ONCE when it has
// something, otherwise HOLDS the request up to `HoldMs` and answers the moment a new journal line for the chat
// lands (it watches the journal's files, ~1 stat a second). So a message shows within ~1-2 s, and an idle
// open tab costs one request every ~25 s instead of one every 3-5 s.
//
// ⛔ PURE - no DOM, no `window`, no `XMLHttpRequest`: the Bridge suite imports this file to pin both halves of
// the wire together (`LiveRun.test.ts`'s contract case), and a transport here would drag the DOM into a Node
// typecheck (`AppAssistPanel`'s lesson).
//
// ⛔ READ-ONLY (decision 9 untouched): nothing here, nor in `AppLiveFeed`, reaches a sender.
//
// ⭐ THE TRANSLATION SEAM: every item carries `Original` and `Needed` beside `Text` (the wire's `original`,
// `needed`). Today the service sends `Text` = the message's words, `Original` "" and `Needed` false. The
// translation step fills them (`Text` = the owner's language, `Original` = what was written, `Needed` = it was
// translated), and `Shown` / `OriginalOf` are the ONE place the page decides what a bubble says and what its
// flag reveals - nothing else reads those fields.
//
// ⚠ DORMANT BY THE SERVICE (`WAC_LIVE`): with the route off, `/live` answers the status document (no boolean
// `ok`) and `Parse` says `Off` - the page then never asks again in this visit (`AppLiveFeed.Off`): no retry
// storm, no sentence, nothing drawn.

/** One incoming (or phone-sent) message for the open chat, as the page draws it. */
export class AppLiveItem {
    /** The WhatsApp key id - becomes the bubble's `data-mid`, the page's dedupe key. */
    public Id: string = "";
    /** The message's own time (ISO, UTC). */
    public At: string = "";
    public FromMe: boolean = false;
    /** What the bubble says. ⭐ Translation seam: the owner's language once the translation step exists. */
    public Text: string = "";
    /** ⭐ Translation seam: what was actually written, when `Text` is a translation; "" today. */
    public Original: string = "";
    /** ⭐ Translation seam: true when `Text` differs from what was written (the bubble then gets a flag). */
    public Needed: boolean = false;
    /** A media message's placeholder line ("photo (not downloaded)") - the page's own wording; "" for text. */
    public Media: string = "";
    /** ⭐ Its translation is on its way (2026-09-28): the bubble goes NOW as written, a patch follows (`AppLivePatch`). */
    public Pending: boolean = false;
    /** ⭐ (wac-liverx 2026-10-01) The message was edited before the page asked - its bubble gets the "edited" mark. */
    public Edited: boolean = false;
}

/** (wac-liverx) What the cursor needs of anything an answer brought: its own id and its time. */
export interface AppLiveTaken {
    Id: string;
    At: string;
}

/** ⭐ A translation that arrived after its bubble went: swap the words, add the flag (`Needed`), or stop waiting. */
export class AppLivePatch {
    public Id: string = "";
    public Text: string = "";
    public Needed: boolean = false;
}

/** ⭐ One message for the chat LIST (`mode: "list"`): which chat, when, whose - never words. */
export class AppLiveListItem {
    public Id: string = "";
    public At: string = "";
    public FromMe: boolean = false;
    /** The 1:1 chat id - an `index.json` row's `chatId`. */
    public Chat: string = "";
}

/** One `/live` answer, read. */
export class AppLiveAnswer {
    /** The route is real and answered - `Items` holds what is new (maybe nothing). */
    public Ok: boolean = false;
    /** ⛔ The route does not exist here (an older or dormant service, a 404, a status document) - never ask again this visit. */
    public Off: boolean = false;
    /** A refusal that will not change on its own (the neutral sentence, a group, a page not in the index) - stop for this page. */
    public Stop: boolean = false;
    /** Seconds to wait before asking again (a refusal that may clear: not running, too many, not built). 0 = none. */
    public RetrySec: number = 0;
    public Why: string = "";
    public Items: AppLiveItem[] = [];
    /** The service had more than it sent (it sends the newest `MaxItems`). */
    public More: boolean = false;
    /** ⭐ Translations the page waited for that have landed. */
    public Patches: AppLivePatch[] = [];
    /** ⭐ (wac-liverx 2026-10-01, `AppLiveEvents`) Reaction rows and edits / revokes for the open chat - events, never bubbles. */
    public Reactions: AppLiveReaction[] = [];
    public Changes: AppLiveChange[] = [];
    /** ⭐ A `list` answer: `ListItems`, `Names` (chat id -> title, for chats the index has not got) and `Built`. */
    public List: boolean = false;
    public ListItems: AppLiveListItem[] = [];
    public Names: Record<string, string> = {};
    public Built: string = "";
    /** ⭐ A `page` answer: the provisional page of a chat that has none yet, and its title. */
    public Page: string = "";
    public Title: string = "";
    /**
     * ⭐ TYPING (2026-09-30): the answer carried the `typing` field - only a service that knows it, answering a page that
     * sent it. False = leave whatever is shown alone (an older service).
     */
    public TypingKnown: boolean = false;
    /** The open chat's contact: `composing` | `recording` | "" (nothing). */
    public Typing: string = "";
    /** A `list` answer: the 1:1 chats whose contact is typing now. */
    public TypingChats: string[] = [];
    /**
     * ⭐ ACCOUNT STANDING (2026-10-01, `AppStanding`): a `list` answer carried `standing` - the subscriber's own WhatsApp
     * account state. False = leave whatever is shown alone (an older service, or one that cannot tell).
     */
    public StandingKnown: boolean = false;
    public Standing: AppStandingNow = new AppStandingNow();
    /** ⛔ What the next ask ECHOES (`AppStanding.EchoOf`): the service's own state and end time as RECEIVED, read or not. */
    public StandingEcho: string = "";
    /**
     * ⭐ WHATSAPP LINK STATE (2026-10-01, `AppWaLink`): a `list` answer - or the "try again in N s" refusal of a list ask -
     * carried `wa`, the subscriber's own WhatsApp link state. False = the service did not say (an older service, the
     * single-session path, another shard): today's line, nothing claimed about WhatsApp.
     */
    public WaKnown: boolean = false;
    public Wa: AppWaNow = new AppWaNow();
}

export class AppLiveRules {
    /** The route. ⛔ `LiveRules.Route` IS this constant - one string for both halves. */
    public static readonly Route: string = "/live";
    /** How long the service holds a request with nothing new - the page's own XHR has no timeout. */
    public static readonly HoldMs: number = 25000;
    /** At most this many ids go in `seen` (the body ceiling is 4096 bytes). */
    public static readonly MaxSeen: number = 40;
    /**
     * After a lost connection: 1 s, doubling, AT MOST 8 s (2026-09-28). ⛔ The owner's line is 10 s ("anything more than
     * 10 seconds should be broken"): the old 5 s -> 60 s ladder kept a feed dark for up to a minute after a deploy or a
     * network blip, with the service healthy again after seconds. One request every 8 s per tab is still nothing to the
     * throttle (`LiveThrottle.PerNumber`), and a real refusal carries its own `retry`.
     */
    public static readonly BackoffFirstMs: number = 1000;
    public static readonly BackoffMaxMs: number = 8000;
    /** ⭐ The modes (`LiveRules.Modes` reads these): the chat list, a chat with no page yet, its provisional page. */
    public static readonly ListMode: string = "list";
    public static readonly ChatMode: string = "chat";
    public static readonly PageMode: string = "page";
    /** A translation not back after this long is given up on (the bubble keeps what was written). */
    public static readonly WaitMaxMs: number = 90000;
    /** The waiting bubble's mark (styled by `Css` - a quiet "· translating…" after the words, never under the clock). */
    public static readonly WaitAttr: string = "data-wac-trwait";
    /** The pill shown when a message arrives while the reader is scrolled up. */
    /**
     * ⛔ NO TYPED ARROW (design system; audit 2026-10-12 - it read "New message ↓"): the words alone are the button's
     * name, and the arrow that points down is drawn by `Css` (`::after`, a chevron in the text's own colour).
     */
    public static readonly PillText: string = "New message";
    public static readonly PillClass: string = "wac-livepill";
    /** How close to the bottom (px) still counts as "at the bottom" - a reader there follows the conversation. */
    public static readonly BottomSlackPx: number = 48;
    /**
     * ⭐ TYPING (2026-09-30, `Bridge/src/TypingState.ts`): the two states WhatsApp's chat state can say, one list for both
     * halves. On the wire as `typing` - a string for an open chat, an array of chat ids for the list.
     */
    public static readonly TypingStates: string[] = ["composing", "recording"];
    /** The line under the conversation / on the card, and its class (styled by `Css` in the frame, `app.css` on cards). */
    public static readonly TypingClass: string = "wac-typing";
    /** At most this many typing chats in a list ask / answer. */
    public static readonly MaxTyping: number = 50;
    /**
     * The page clears a line the service has not renewed after this long - above the service's own expiry (12 s,
     * `TypingRules.ExpiryMs`), so normally the service's answer clears it first; this only covers a feed that stopped.
     * ⛔ ALSO ABOVE A HELD ASK (`HoldMs`, 25 s): a contact who keeps typing is not a CHANGE, so the service answers only at
     * the deadline - a safety shorter than that cleared the line of somebody still typing until the deadline put it back.
     */
    public static readonly TypingSafetyMs: number = 35000;

    /**
     * The request: the proof, the page (resolved by the service through the subscriber's OWN index), the floor
     * (`after`, an ISO time - "" = no floor) and the ids at or after it the page already holds (`seen`, capped).
     */
    public static Body = (Phone: string, Key: string, Page: string, After: string, Seen: string[]): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return JSON.stringify({ number: nz(Phone).trim(), key: nz(Key).trim(), page: nz(Page).trim(),
                after: nz(After).trim(), seen: AppLiveRules.Ids(Seen) });
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveRules.Body");
            return "";
        }
    };

    /**
     * ⭐ The full request (2026-09-28): `Body`'s fields + the mode (`list` / `chat` / `page`, "" = the open page), the
     * chat of a `chat` / `page` ask, the ids whose translation is awaited (`wait`) and how long the page was not
     * listening (`gap`, ms since its previous answer; -1 = first ask) - what the service's latency book measures by.
     */
    public static BodyOf = (Phone: string, Key: string, Page: string, After: string, Seen: string[], Mode: string,
                            Chat: string, Wait: string[], GapMs: number, Typing: string | string[] | null = null,
                            Standing: string | null = null, Wa: string | null = null, Events: boolean = false): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Doc: Record<string, unknown> = { number: nz(Phone).trim(), key: nz(Key).trim(), page: nz(Page).trim(),
                after: nz(After).trim(), seen: AppLiveRules.Ids(Seen), gap: Math.max(-1, Math.floor(GapMs)) };
            if (nz(Mode).length > 0) { Doc.mode = nz(Mode); }
            if (nz(Chat).length > 0) { Doc.chat = nz(Chat).trim(); }
            if (Wait.length > 0) { Doc.wait = AppLiveRules.Ids(Wait); }
            // ⭐ WHAT THE PAGE SHOWS NOW: the service answers early only when its typing state differs (`LiveRun`). A page
            // that sends nothing here is never answered early - no loop against an older page.
            if (typeof Typing === "string") { Doc.typing = AppLiveRules.TypingOf(Typing); }
            if (Array.isArray(Typing) === true) { Doc.typing = AppLiveRules.TypingChatsOf(Typing as unknown[]); }
            // ⭐ THE ACCOUNT WARNING THE PAGE SHOWS NOW (`AppStanding.SigOf`; the list feed only): the same rule as `typing` -
            // answered early only when the service's state differs, and a page that sends nothing is never answered early.
            // ⛔ `EchoOk`, not `SigIn`: the echo of an end time this build cannot READ must still go back (version skew).
            if (typeof Standing === "string" && AppStanding.EchoOk(Standing) === true) { Doc.standing = Standing; }
            // ⭐ THE WHATSAPP LINK STATE WORD THE PAGE HOLDS (`AppWaLink`; the list feed only) - the same rule again: the
            // word RECEIVED goes back ("unknown" at first), and a page that sends nothing is never answered early.
            if (typeof Wa === "string" && AppWaLink.StateOk(Wa) === true) { Doc.wa = Wa; }
            // ⛔ "I TAKE REACTIONS, EDITS AND REVOKES" (`events`, the open chat's feed only; verifier 2026-10-01) - the same
            // rule once more: the service sends events, and answers a held request for one, ONLY to a page that says this.
            // A page from before 2026-10-01 never moves its floor past an event it cannot read, so it would be answered
            // at once on every ask - three requests a second, per open tab, from the moment the first reaction landed.
            if (Events === true) { Doc.events = true; }
            return JSON.stringify(Doc);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveRules.BodyOf");
            return "";
        }
    };

    /** Ids trimmed, 1..64 characters, once each, the newest `MaxSeen`. */
    private static Ids = (Raw: string[]): string[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const Ids: string[] = [];
            for (const One of Raw) {
                const Id: string = nz(One).trim();
                if (Id.length > 0 && Id.length <= 64 && Ids.indexOf(Id) < 0) { Ids.push(Id); }
            }
            return Ids.slice(Math.max(0, Ids.length - AppLiveRules.MaxSeen));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveRules.Ids");
            return [];
        }
    };

    /**
     * Read an answer. ⛔ ONLY `ok === true` WITH `live === true` IS THE ROUTE: a status document, a 404, an
     * HTML error page or an answer with no boolean `ok` is `Off` (the feature is not here - never retried).
     * Status 0 / 5xx = a lost connection or our end: neither Ok nor Off nor Stop (the caller backs off).
     */
    public static Parse = (Status: number, Text: string): AppLiveAnswer => {
        const Out: AppLiveAnswer = new AppLiveAnswer();
        if (getCoreUtils().KErrorMode === true) { return Out; }
        try {
            if (Status === 0 || Status >= 500) { return Out; }
            let Raw: unknown = null;
            try {
                Raw = JSON.parse(nz(Text));
            } catch (Bad) {
                // ERROR-SUPPRESSED-JUSTIFIED: an answer that is not JSON (a 404 page, a proxy's HTML) is an EXPECTED
                // answer from a service without the route - law 1, fate 1: the feature is simply not here.
                Out.Off = true;
                return Out;
            }
            if (Raw === null || typeof Raw !== "object" || Array.isArray(Raw) === true) { Out.Off = true; return Out; }
            const Doc: Record<string, unknown> = Raw as Record<string, unknown>;
            if (typeof Doc.ok !== "boolean" || Status !== 200) { Out.Off = true; return Out; }
            if (Doc.ok === false) {
                if (typeof Doc.why === "string") { Out.Why = Doc.why; }
                if (typeof Doc.retry === "number" && Number.isFinite(Doc.retry) === true && Doc.retry > 0) {
                    Out.RetrySec = Math.min(600, Math.ceil(Doc.retry));
                    // ⭐ A "try again" answer may still say how WhatsApp is doing ("starting", "stopped") - `LiveRules.LaterWith`.
                    AppLiveRules.WaIn(Doc, Out);
                } else {
                    Out.Stop = true;
                }
                return Out;
            }
            if (Doc.live !== true) { Out.Off = true; return Out; }
            if (typeof Doc.page === "string") {
                Out.Ok = true;
                Out.Page = Doc.page;
                if (typeof Doc.title === "string") { Out.Title = Doc.title; }
                return Out;
            }
            if (Array.isArray(Doc.items) === false) { Out.Off = true; return Out; }
            Out.Ok = true;
            Out.More = Doc.more === true;
            AppLiveRules.TypingIn(Doc, Out);
            if (Doc.list === true) { AppLiveRules.ListIn(Doc, Out); return Out; }
            for (const One of Doc.items as unknown[]) {
                const Item: AppLiveItem | null = AppLiveRules.ItemOf(One);
                if (Item !== null) { Out.Items.push(Item); }
            }
            if (Array.isArray(Doc.patches) === true) { Out.Patches = AppLiveRules.PatchesOf(Doc.patches as unknown[]); }
            // (wac-liverx) Absent on an older service: both stay empty and the page behaves as before.
            Out.Reactions = AppLiveEvents.ReactionsOf(Doc.reactions);
            Out.Changes = AppLiveEvents.ChangesOf(Doc.changes);
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveRules.Parse");
            return new AppLiveAnswer();
        }
    };

    /** One wire item, or null. ⛔ Every field with its own `typeof`, never coerced; an id-less item is dropped. */
    public static ItemOf = (Raw: unknown): AppLiveItem | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            if (Raw === null || typeof Raw !== "object" || Array.isArray(Raw) === true) { return null; }
            const Doc: Record<string, unknown> = Raw as Record<string, unknown>;
            if (typeof Doc.id !== "string" || /^[A-Za-z0-9_-]{1,64}$/.test(Doc.id) === false) { return null; }
            if (typeof Doc.at !== "string" || isNaN(Date.parse(Doc.at)) === true) { return null; }
            const Out: AppLiveItem = new AppLiveItem();
            Out.Id = Doc.id;
            Out.At = Doc.at;
            Out.FromMe = Doc.fromMe === true;
            if (typeof Doc.text === "string") { Out.Text = Doc.text; }
            if (typeof Doc.original === "string") { Out.Original = Doc.original; }
            Out.Needed = Doc.needed === true;
            Out.Pending = Doc.pending === true;
            Out.Edited = Doc.edited === true;
            if (typeof Doc.media === "string") { Out.Media = Doc.media; }
            if (Out.Text.length === 0 && Out.Media.length === 0) { return null; }
            // ⭐ (2026-10-14) Quote, "Forwarded", map, poll tally... kept until the bubble is drawn (`AppBubbleKinds.Decorate`).
            AppBubbleKinds.Remember(Out.Id, Doc.kinds);
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveRules.ItemOf");
            return null;
        }
    };

    /** The landed translations. ⛔ Each field with its own `typeof`; a translated patch with no words is dropped. */
    public static PatchesOf = (Raw: unknown[]): AppLivePatch[] => {
        const Out: AppLivePatch[] = [];
        if (getCoreUtils().KErrorMode === true) { return Out; }
        try {
            for (const One of Raw) {
                if (One === null || typeof One !== "object" || Array.isArray(One) === true) { continue; }
                const Doc: Record<string, unknown> = One as Record<string, unknown>;
                if (typeof Doc.id !== "string" || /^[A-Za-z0-9_-]{1,64}$/.test(Doc.id) === false) { continue; }
                const Patch: AppLivePatch = new AppLivePatch();
                Patch.Id = Doc.id;
                Patch.Needed = Doc.needed === true;
                if (typeof Doc.text === "string") { Patch.Text = Doc.text; }
                if (Patch.Needed === true && Patch.Text.trim().length === 0) { continue; }
                Out.push(Patch);
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveRules.PatchesOf");
            return [];
        }
    };

    /** A `list` answer's items (id, time, whose, a 1:1 chat - no words), names and `built`. */
    private static ListIn = (Doc: Record<string, unknown>, Out: AppLiveAnswer): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            Out.List = true;
            if (typeof Doc.built === "string") { Out.Built = Doc.built; }
            const Standing: AppStandingNow | null = AppStanding.Parse(Doc.standing);
            if (Standing !== null) {
                Out.StandingKnown = true;
                Out.Standing = Standing as AppStandingNow;
                Out.StandingEcho = nz(AppStanding.EchoOf(Doc.standing));
            }
            AppLiveRules.WaIn(Doc, Out);
            for (const One of Doc.items as unknown[]) {
                if (One === null || typeof One !== "object" || Array.isArray(One) === true) { continue; }
                const Raw: Record<string, unknown> = One as Record<string, unknown>;
                if (typeof Raw.id !== "string" || /^[A-Za-z0-9_-]{1,64}$/.test(Raw.id) === false) { continue; }
                if (typeof Raw.at !== "string" || isNaN(Date.parse(Raw.at)) === true) { continue; }
                if (typeof Raw.chat !== "string" || AppLiveRules.ChatIdOk(Raw.chat) === false) { continue; }
                const Item: AppLiveListItem = new AppLiveListItem();
                Item.Id = Raw.id;
                Item.At = Raw.at;
                Item.FromMe = Raw.fromMe === true;
                Item.Chat = Raw.chat;
                Out.ListItems.push(Item);
            }
            if (Doc.names === null || typeof Doc.names !== "object" || Array.isArray(Doc.names) === true) { return; }
            const Names: Record<string, unknown> = Doc.names as Record<string, unknown>;
            for (const Chat of Object.keys(Names)) {
                if (AppLiveRules.ChatIdOk(Chat) === true && typeof Names[Chat] === "string") { Out.Names[Chat] = (Names[Chat] as string).substring(0, 120); }
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveRules.ListIn");
        }
    };

    /** ⭐ The answer's `wa` (`{state, since?}`). Absent or not one = not known: nothing about WhatsApp is claimed. */
    private static WaIn = (Doc: Record<string, unknown>, Out: AppLiveAnswer): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Wa: AppWaNow | null = AppWaLink.Parse(Doc.wa);
            if (Wa === null) { return; }
            Out.WaKnown = true;
            Out.Wa = Wa as AppWaNow;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveRules.WaIn");
        }
    };

    /** ⭐ The answer's `typing`: a string (open chat) or an array of 1:1 chat ids (list). Absent = not known (older service). */
    private static TypingIn = (Doc: Record<string, unknown>, Out: AppLiveAnswer): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (typeof Doc.typing === "string") { Out.TypingKnown = true; Out.Typing = AppLiveRules.TypingOf(Doc.typing); return; }
            if (Array.isArray(Doc.typing) === true) { Out.TypingKnown = true; Out.TypingChats = AppLiveRules.TypingChatsOf(Doc.typing as unknown[]); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveRules.TypingIn");
        }
    };

    /** A typing state as the wire may carry it: one of `TypingStates`, anything else "". DOCUMENTED EXCEPTION: pure.
     *  ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    public static TypingOf = (Raw: unknown): string => {
        if (typeof Raw !== "string" || AppLiveRules.TypingStates.indexOf(Raw) < 0) { return ""; }
        return Raw;
    };

    /** Typing chats as the wire may carry them: 1:1 ids only, once each, sorted, at most `MaxTyping`. */
    public static TypingChatsOf = (Raw: unknown[]): string[] => {
        const Out: string[] = [];
        if (getCoreUtils().KErrorMode === true) { return Out; }
        try {
            for (const One of Raw) {
                if (typeof One !== "string" || AppLiveRules.ChatIdOk(One) === false || Out.indexOf(One) >= 0) { continue; }
                if (Out.length < AppLiveRules.MaxTyping) { Out.push(One); }
            }
            return Out.sort();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveRules.TypingChatsOf");
            return [];
        }
    };

    /** What the line says: "typing…" / "recording audio…"; "" for nothing. DOCUMENTED EXCEPTION: pure lookup.
     *  ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    public static TypingText = (State: string): string => {
        if (State === "composing") { return "typing…"; }
        if (State === "recording") { return "recording audio…"; }
        return "";
    };

    /**
     * ⛔ A ONE-TO-ONE CHAT ID - the service's own rule (`LiveRules.ChatIdOk`, pinned by the contract test).
     * DOCUMENTED EXCEPTION: pure pattern test. ERROR-SUPPRESSED-JUSTIFIED: cannot throw.
     */
    public static ChatIdOk = (Id: string): boolean => {
        return /^\d{5,20}@(s\.whatsapp\.net|lid)$/.test(nz(Id));
    };

    /**
     * ⭐ THE TRANSLATION SEAM (read side): what the bubble says. Today `Text`; the translation step decides here
     * between the translation and the original. DOCUMENTED EXCEPTION: a field read.
     * ERROR-SUPPRESSED-JUSTIFIED: cannot throw.
     */
    public static Shown = (Item: AppLiveItem): string => {
        return Item.Text;
    };

    /**
     * ⭐ THE TRANSLATION SEAM (flag side): the original the bubble's flag would reveal, or "" (no flag). Only a
     * translated item has one. DOCUMENTED EXCEPTION: a field read. ERROR-SUPPRESSED-JUSTIFIED: cannot throw.
     */
    public static OriginalOf = (Item: AppLiveItem): string => {
        if (Item.Needed !== true) { return ""; }
        return Item.Original;
    };

    /** The 12-digit UTC minute of a page bubble id (`m-YYYYMMDDHHMM` or `-n`) -> an ISO floor; "" when none. */
    public static FloorOf = (Minute: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const M: string = nz(Minute);
            if (/^\d{12}$/.test(M) === false) { return ""; }
            return M.substring(0, 4) + "-" + M.substring(4, 6) + "-" + M.substring(6, 8) + "T" + M.substring(8, 10)
                + ":" + M.substring(10, 12) + ":00.000Z";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveRules.FloorOf");
            return "";
        }
    };

    /** The next wait after `Failures` lost connections in a row: 1 s, 2 s, 4 s, then 8 s at most. */
    public static BackoffMs = (Failures: number): number => {
        if (getCoreUtils().KErrorMode === true) { return AppLiveRules.BackoffMaxMs; }
        try {
            const N: number = Math.max(1, Math.min(8, Math.floor(Failures)));
            return Math.min(AppLiveRules.BackoffMaxMs, AppLiveRules.BackoffFirstMs * Math.pow(2, N - 1));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveRules.BackoffMs");
            return AppLiveRules.BackoffMaxMs;
        }
    };

    /**
     * Is a reversed (`column-reverse`) scroll pane at its bottom? Chrome/Firefox/Safari put `scrollTop` 0 at the
     * bottom of a reversed pane and go NEGATIVE going up; an engine that still counts from the top is covered by
     * the distance to `scrollHeight - clientHeight`. DOCUMENTED EXCEPTION: arithmetic on three numbers in hand.
     * ERROR-SUPPRESSED-JUSTIFIED: pure, cannot throw.
     */
    public static AtBottom = (ScrollTop: number, ScrollHeight: number, ClientHeight: number): boolean => {
        if (Math.abs(ScrollTop) <= AppLiveRules.BottomSlackPx) { return true; }
        return Math.abs(ScrollHeight - ClientHeight - ScrollTop) <= AppLiveRules.BottomSlackPx && ScrollTop > 0;
    };

    /**
     * The pill's stylesheet - rides in the frame's linked sheet (`AppMain.Frame`, beside `AppChatAppend.Css`).
     * ⛔ Never an inline style (the CSP has no 'unsafe-inline'). DOCUMENTED EXCEPTION: a constant stylesheet.
     * ERROR-SUPPRESSED-JUSTIFIED: returns a literal.
     */
    public static Css = (): string => {
        return ".thread{position:relative}"
            + "." + AppLiveRules.PillClass + "{position:absolute;left:50%;bottom:14px;transform:translateX(-50%);"
            // ⭐ The business look (2026-10-13): a small-radius button with an edge - not a pill - in the app's own colours
            // (the fallbacks were WhatsApp's). It floats over the messages, so it keeps one quiet shadow.
            + "z-index:5;border:1px solid var(--line-strong,#858c99);border-radius:6px;padding:6px 14px;font:inherit;font-size:13px;"
            + "font-weight:600;cursor:pointer;background:var(--bg,#fff);color:var(--acc,#095495);box-shadow:0 2px 8px rgba(16,20,28,.16)}"
            + "." + AppLiveRules.PillClass + "[hidden]{display:none}"
            + "." + AppLiveRules.PillClass + "::after{content:\"\";display:inline-block;width:6px;height:6px;margin:0 2px 3px 9px;"
            + "border-right:2px solid currentColor;border-bottom:2px solid currentColor;transform:rotate(45deg)}"
            // ⭐ A bubble whose translation is on its way (it went at once, as written): a quiet word before the clock.
            // ⚠ The page reserves the clock's room with `.b:has(>.txt+.t)>.txt::after` (`DossierHtml`) - this rule is MORE
            // specific on purpose, and keeps that room itself (the padding), so the clock never covers the word.
            + ".b[" + AppLiveRules.WaitAttr + "=\"1\"]:has(>.txt+.t)>.txt::after,.b[" + AppLiveRules.WaitAttr + "=\"1\"]>.txt::after"
            + "{content:\" \\00b7  translating\\2026\";display:inline;width:auto;height:auto;padding-right:44px;"
            + "font-size:11.5px;font-style:italic;color:var(--dim,#5c6370)}"
            // ⭐ TYPING (2026-09-30): the contact's "typing…" line - the FIRST child of the reversed `.chat`, i.e. the
            // bottom of the conversation, under the newest bubble. Accent colour (the app's `--acc` when the page has it).
            + "." + AppLiveRules.TypingClass + "{align-self:flex-start;margin:2px 10px 8px;padding:5px 12px;border-radius:6px;"
            + "background:var(--inbg,#fff);color:var(--acc,#095495);font-size:13px;font-style:italic;"
            + "border:1px solid var(--line,#e3e6ec)}"
            + "." + AppLiveRules.TypingClass + ":empty{display:none}";
    };
}

/**
 * What the page already holds, and the floor it asks from. ⭐ `after` only ever moves FORWARD (the newest `at`
 * seen); `seen` is every id at or after it, so two messages in the same second are never lost nor drawn twice.
 */
export class AppLiveCursor {
    private After: string = "";
    private AfterMs: number = NaN;
    private Held: Record<string, number> = {};

    /** Start from the page: its newest minute (`FloorOf`) and the `data-mid`s of the bubbles in it. */
    public Begin = (Floor: string, Ids: string[]): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.After = nz(Floor);
            this.AfterMs = Date.parse(this.After);
            let At: number = 0;
            if (isNaN(this.AfterMs) === false) { At = this.AfterMs; }
            for (const Id of Ids) { this.Held[nz(Id)] = At; }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveCursor.Begin");
        }
    };

    /**
     * Remember what an answer brought, and move the floor to the newest of it. ⭐ (wac-liverx) Anything with an id and
     * a time: a message, a reaction row, an edit or a revoke - an event is never asked for twice either.
     */
    public Took = (Items: AppLiveTaken[]): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            for (const Item of Items) {
                const Ms: number = Date.parse(Item.At);
                this.Held[Item.Id] = Ms;
                if (isNaN(this.AfterMs) === true || Ms > this.AfterMs) { this.AfterMs = Ms; this.After = Item.At; }
            }
            // Forget ids below the floor - the service never answers them again.
            for (const Id of Object.keys(this.Held)) {
                if (isNaN(this.AfterMs) === false && this.Held[Id] < this.AfterMs) { delete this.Held[Id]; }
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveCursor.Took");
        }
    };

    /** The floor the next request asks from ("" = none). DOCUMENTED EXCEPTION: a field read.
     *  ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    public Floor = (): string => {
        return this.After;
    };

    /** The ids at or after the floor already held - the next request's `seen`. */
    public Seen = (): string[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            return Object.keys(this.Held).slice(-AppLiveRules.MaxSeen);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveCursor.Seen");
            return [];
        }
    };

    /** Was this id already taken (by the page or an earlier answer)? DOCUMENTED EXCEPTION: a lookup.
     *  ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    public Has = (Id: string): boolean => {
        return Object.prototype.hasOwnProperty.call(this.Held, Id) === true;
    };
}
